import {
  FunctionComponent,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Dialog,
  FormControlLabel,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import AppStateContext from "../../../contexts/AppStateContext";
import styles from "./Styles/loginPageStyle.module.css";
import {
  BusinessCenter,
  Email,
  Face,
  LocationCity,
  Phone,
} from "@mui/icons-material";
import {
  REGEX_ADDRESS,
  REGEX_EMAIL,
  REGEX_FIRSTNAME,
  REGEX_LASTNAME,
} from "../../../util/RegExUtil";
import { apiCreateUser, apiUpdateUser } from "../../../util/network/Users";
import {
  validateCVR,
  validateEAN,
  validatePhone,
  validatePostalCode,
} from "../../../util/validation";
import ValidityIndicator from "components/ValidityIndicator/ValidityIndicator";
import { AccessEnum, CreateUserType, UserType } from "types/users/UserTypes";
import { AddressType } from "../../../types/AddressTypes";
import { UserUpdateType } from "types/users/UserCommandsType";
import { useToast } from "contexts/ToastContext";
import CompanySearch, {
  CompanyData,
} from "components/CompanySearch/CompanySearch";
import CountrySelect from "components/CountrySelect/CountrySelect";

/**
 * Placeholder `createAddressInfo` writes for address fields left empty. It is
 * stored verbatim on the customer, so it comes back when editing and has to be
 * stripped — otherwise validation rejects a value the app itself wrote.
 */
const ADDRESS_PLACEHOLDER = "Not provided";

const stripPlaceholder = (value?: string | null): string =>
  !value || value === ADDRESS_PLACEHOLDER ? "" : value;

type PropsType = {
  setCustomer: (customer: UserType) => void;
  existing?: UserType | null;
  accessLevel?: AccessEnum | null;
  onSubmit?: Function;
  onClose?: Function;
};

const UserModal: FunctionComponent<PropsType> = ({
  setCustomer,
  existing,
  accessLevel,
  onSubmit,
  onClose,
}) => {
  const { showUserModal, setShowUserModal, setShowLoadingModal } =
    useContext(AppStateContext);
  const { addToast } = useToast();
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  // const [jobTitle, setJobTitle] = useState<string>("");

  const [billingEmail, setBillingEmail] = useState<string>("");
  const [company, setCompany] = useState<string | undefined>("");
  const [addressName, setAddressName] = useState<string>("");
  const [zipCode, setZipCode] = useState<string | undefined>("");
  const [city, setCity] = useState<string | undefined>("");
  const [phone, setPhone] = useState<string | undefined>("");
  const [cvr, setCvr] = useState<string | undefined>("");
  const [ean, setEan] = useState<string>("");

  const [billingCountryId, setBillingCountryId] = useState<string>("DK");
  const [initials, setInitials] = useState<string>("");
  const [useSameForShipping, setUseSameForShipping] = useState<boolean>(true);
  const [shippingAddressName, setShippingAddressName] = useState<string>("");
  const [shippingZipCode, setShippingZipCode] = useState<string | undefined>("");
  const [shippingCity, setShippingCity] = useState<string | undefined>("");
  const [shippingPhone, setShippingPhone] = useState<string | undefined>("");
  const [shippingEmail, setShippingEmail] = useState<string>("");
  const [shippingCountryId, setShippingCountryId] = useState<string>("DK");

  /** Whether a submit was attempted — gates the required-field errors. */
  const [submitAttempted, setSubmitAttempted] = useState<boolean>(false);
  /** Error message returned by the backend on the last submit. */
  const [submitError, setSubmitError] = useState<string | null>(null);
  /** Scroll target so a failure is visible from the bottom of a long form. */
  const errorRef = useRef<HTMLDivElement | null>(null);

  const { token } = useContext(AppStateContext);

  useEffect(() => {
    // Errors belong to the customer that was on screen, not the next one.
    setSubmitAttempted(false);
    setSubmitError(null);

    if (existing) {
      setFirstName(existing.firstName);
      setLastName(existing.lastName);
      setEmail(existing.email);
      setEan(existing.eanNumber ?? "");
      setInitials(existing.initials ?? "");

      if (existing.defaultBillingAddress) {
        setBillingEmail(stripPlaceholder(existing.defaultBillingAddress.email));
        setCompany(stripPlaceholder(existing.defaultBillingAddress.company));
        setAddressName(stripPlaceholder(existing.defaultBillingAddress.street));
        setZipCode(stripPlaceholder(existing.defaultBillingAddress.postalCode));
        setCity(stripPlaceholder(existing.defaultBillingAddress.city));
        setPhone(stripPlaceholder(existing.defaultBillingAddress.phone));
        setCvr(stripPlaceholder(existing.defaultBillingAddress.vat));
        setBillingCountryId(existing.defaultBillingAddress.countryCode || "DK");
      }
      if (existing.defaultShippingAddress) {
        setShippingAddressName(stripPlaceholder(existing.defaultShippingAddress.street));
        setShippingZipCode(stripPlaceholder(existing.defaultShippingAddress.postalCode));
        setShippingCity(stripPlaceholder(existing.defaultShippingAddress.city));
        setShippingPhone(stripPlaceholder(existing.defaultShippingAddress.phone));
        setShippingEmail(stripPlaceholder(existing.defaultShippingAddress.email));
        setShippingCountryId(existing.defaultShippingAddress.countryCode || "DK");
      }

      const inputsToCompare: (keyof AddressType)[] = [
        "firstName", "lastName", "email", "company",
        "street", "postalCode", "city", "phone", "vat",
      ];
      const areAddressesSame =
        existing.defaultBillingAddress != null &&
        existing.defaultShippingAddress != null &&
        inputsToCompare.every(
          (key) =>
            existing.defaultBillingAddress![key] ===
            existing.defaultShippingAddress![key]
        );
      setUseSameForShipping(areAddressesSame);
    } else if (existing === null) {
      clearData();
    }
  }, [existing]);

  const handleCompanySelect = (data: CompanyData) => {
    setCompany(data.name);
    setCvr(data.vat.toString());
    setAddressName(data.address);
    setZipCode(data.zipcode.toString());
    setCity(data.city);
    setPhone(data.phone?.toString());
  };

  const clearData = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setInitials("");
    // setJobTitle("");
    setBillingEmail("");
    setCompany("");
    setAddressName("");
    setZipCode("");
    setCity("");
    setPhone("");
    setCvr("");
    setEan("");
    setUseSameForShipping(true);
    setShippingAddressName("");
    setShippingZipCode("");
    setShippingCity("");
    setShippingPhone("");
    setShippingEmail("");
    setBillingCountryId("DK");
    setShippingCountryId("DK");
  };

  const createAddressInfo = (
    firstName: string,
    lastName: string,
    email: string,
    street: string,
    company?: string,
    phone?: string,
    zipCode?: string,
    vat?: string,
    city?: string,
    countryCode?: string
  ): AddressType => ({
    firstName,
    lastName,
    email,
    street,
    company: company || "Not provided",
    phone: phone || "Not provided",
    postalCode: zipCode || "Not provided",
    vat: vat || "Not provided",
    city: city || "Not provided",
    countryCode: countryCode || "DK",
  });

  /**
   * The single source of truth for field validation — the inputs read their
   * error state from here rather than repeating the rules inline.
   *
   * The backend only validates CVR and EAN (both 422); phone and postal code
   * are persisted unchecked, so they have to be caught here or bad data saves
   * silently.
   *
   * `includeRequired` is off while the user is still filling the form, so
   * empty fields do not light up red before the first submit attempt.
   *
   * @returns a message per field id, so the offending input can be highlighted.
   */
  const collectErrors = (includeRequired: boolean): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (!billingEmail) {
      if (includeRequired) errors.billingEmail = "Invoicing email is required";
    } else if (!REGEX_EMAIL.test(billingEmail)) {
      errors.billingEmail = "Invoicing email is not a valid email address";
    }

    if (!firstName) {
      if (includeRequired) errors.firstName = "First name is required";
    } else if (!REGEX_FIRSTNAME.test(firstName)) {
      errors.firstName = "Invalid first name";
    }

    if (!lastName) {
      if (includeRequired) errors.lastName = "Last name is required";
    } else if (!REGEX_LASTNAME.test(lastName)) {
      errors.lastName = "Invalid last name";
    }

    if (!addressName) {
      if (includeRequired) errors.billingStreet = "Company street is required";
    } else if (!REGEX_ADDRESS.test(addressName)) {
      errors.billingStreet = "Invalid address";
    }

    // The contact email is immutable once the user exists, so it is only
    // validated while creating.
    if (!existing) {
      if (!email) {
        if (includeRequired) errors.contactEmail = "Email is required";
      } else if (!REGEX_EMAIL.test(email)) {
        errors.contactEmail = "Email is not a valid email address";
      }
    }

    const cvrError = cvr ? validateCVR(cvr, billingCountryId) : undefined;
    if (cvrError) errors.cvr = cvrError;

    const eanError = ean ? validateEAN(ean, billingCountryId) : undefined;
    if (eanError) errors.ean = eanError;

    const zipError = validatePostalCode(zipCode, billingCountryId);
    if (zipError) errors.billingZip = `Billing ${zipError.toLowerCase()}`;

    const phoneError = validatePhone(phone);
    if (phoneError) errors.billingPhone = `Billing ${phoneError.toLowerCase()}`;

    if (!useSameForShipping) {
      if (shippingEmail && !REGEX_EMAIL.test(shippingEmail))
        errors.shippingEmail = "Shipment email is not a valid email address";

      if (shippingAddressName && !REGEX_ADDRESS.test(shippingAddressName))
        errors.shippingStreet = "Invalid address";

      const shipZipError = validatePostalCode(shippingZipCode, shippingCountryId);
      if (shipZipError)
        errors.shippingZip = `Shipment ${shipZipError.toLowerCase()}`;

      const shipPhoneError = validatePhone(shippingPhone);
      if (shipPhoneError)
        errors.shippingPhone = `Shipment ${shipPhoneError.toLowerCase()}`;
    }

    return errors;
  };

  /**
   * Errors shown right now. Derived on every render, so fixing a field clears
   * its error immediately instead of leaving a stale message from the last
   * submit. Required-field errors only appear once a submit was attempted.
   */
  const fieldErrors = collectErrors(submitAttempted);

  const handleUserCreationSuccess = (user: UserType) => {
    setCustomer(user);
    setShowUserModal(false);
    addToast({
      type: "success",
      message: (
        <>
          New user was <strong>created</strong> successfully.
        </>
      ),
    });
    onSubmit && onSubmit();
  };

  /**
   * Surfaces a failed request. `FetchError.message` carries the backend's own
   * text (e.g. "Invalid CVR", "User is not authorized to access resource."),
   * so show it verbatim rather than a generic network message.
   *
   * Reported both in the dialog and as a sticky toast: the dialog is taller
   * than the viewport, so the banner alone can sit off-screen above the Save
   * button the user just pressed.
   */
  const handleRequestError = (action: "create" | "update", detail: string) => {
    const message = `Could not ${action} the customer: ${detail}`;
    setSubmitError(message);
    addToast({ type: "error", message, keep: true });
    errorRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };

  /** Extracts a message from a rejected request, however it failed. */
  const errorDetail = (error: unknown): string =>
    error instanceof Error && error.message ? error.message : String(error);

  /**
   * A 2xx whose body is an error envelope rather than a user. The backend
   * answers some failures with 200 + `{"message": ...}`, which would otherwise
   * be stored as the customer and reported as a success.
   */
  const responseError = (response: any): string | null => {
    if (response && typeof response === "object" && response.id != null)
      return null;
    return (response && response.message) || "Unexpected response from server";
  };

  const createUser = async (customerData: CreateUserType) => {
    try {
      const response = await apiCreateUser(token, customerData);
      const error = responseError(response);
      if (error) {
        console.error("Failed to create user:", response);
        handleRequestError("create", error);
        return;
      }
      handleUserCreationSuccess(response);
    } catch (error) {
      console.error("Failed to create user:", error);
      handleRequestError("create", errorDetail(error));
    }
  };

  const updateUser = async (userId: number, changes: UserUpdateType) => {
    try {
      const response = await apiUpdateUser(token, userId, changes);
      const error = responseError(response);
      if (error) {
        console.error("Failed to update user:", response);
        handleRequestError("update", error);
        return;
      }
      setCustomer(response);
      setShowUserModal(false);
      addToast({
        type: "success",
        message: (
          <>
            User was <strong>updated</strong> successfully.
          </>
        ),
      });
      onSubmit && onSubmit();
    } catch (error) {
      console.error("Failed to update user:", error);
      handleRequestError("update", errorDetail(error));
    }
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    setSubmitAttempted(true);
    if (Object.keys(collectErrors(true)).length > 0) {
      errorRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      return;
    }

    setShowLoadingModal(true);

    try {
      const billingAddressInfo = createAddressInfo(
        firstName,
        lastName,
        billingEmail,
        addressName,
        company,
        phone,
        zipCode,
        cvr,
        city,
        billingCountryId
      );

      const shippingAddressInfo = useSameForShipping
        ? billingAddressInfo
        : createAddressInfo(
            firstName,
            lastName,
            shippingEmail || billingEmail,
            shippingAddressName || addressName,
            company,
            shippingPhone,
            shippingZipCode,
            cvr,
            shippingCity,
            shippingCountryId
          );

      if (existing) {
        const changes: UserUpdateType = {
          firstName,
          lastName,
          billingAddress: billingAddressInfo,
          shippingAddress: shippingAddressInfo,
          eanNumber: ean,
          initials: initials || undefined,
        };
        await updateUser(existing.id, changes);
      } else {
        const customerData: CreateUserType = {
          firstName,
          lastName,
          email,
          address: billingAddressInfo,
          shippingAddress: shippingAddressInfo,
          accessLevel: accessLevel || AccessEnum.CUSTOMER,
          eanNumber: ean,
          initials: initials || undefined,
        };
        await createUser(customerData);
      }
    } finally {
      setShowLoadingModal(false);
    }
  };

  const handleClose = () => {
    setSubmitAttempted(false);
    setSubmitError(null);
    onClose && onClose();
    setShowUserModal(false);
  };

  return (
    <Dialog open={showUserModal} onClose={() => handleClose()} maxWidth="md">
      <div className={styles.userModalContainer}>
        <div className={`${styles.pdLeft} ${styles.pdbottom}`}>
          <h3>{existing ? "UPDATING CUSTOMER" : "CREATING CUSTOMER"}</h3>
          <span className={styles.requiredText}>{"*Required"}</span>
        </div>

        <div ref={errorRef}>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2, whiteSpace: "pre-line" }}>
              <AlertTitle>
                {existing ? "Update failed" : "Creation failed"}
              </AlertTitle>
              {submitError}
            </Alert>
          )}
          {submitAttempted && Object.keys(fieldErrors).length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <AlertTitle>Please correct the following</AlertTitle>
              <ul style={{ margin: 0, paddingLeft: "1.2em" }}>
                {Object.entries(fieldErrors).map(([id, message]) => (
                  <li key={id}>{message}</li>
                ))}
              </ul>
            </Alert>
          )}
        </div>
        <form id={"addressSubmit"}>
          {/* Section 1: Company Search */}
          <div style={{ marginBottom: "1rem" }}>
            <CompanySearch onCompanySelect={handleCompanySelect} />
          </div>

          {/* Section 2: Company Info */}
          <div className={styles.billingShippingContainer}>
            <div className={styles.twoColumnField}>
              <TextField
                label={"Company name:"}
                id="company"
                size="small"
                type="text"
                fullWidth
                value={company}
                onChange={(e) => {
                  if (e.target.value.length < 45) setCompany(e.target.value);
                }}
              />
            </div>
            <div className={styles.twoColumnField}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <TextField
                  label={"CVR:"}
                  id="cvr"
                  size="small"
                  type="text"
                  fullWidth
                  value={cvr}
                  onChange={(e) => setCvr(e.target.value)}
                  error={!!fieldErrors.cvr}
                  helperText={fieldErrors.cvr ?? ""}
                />
                <ValidityIndicator
                  valid={cvr && validateCVR(cvr, billingCountryId) !== undefined ? false : null}
                  warningTooltip="CVR checksum is invalid — check the number"
                />
                <ValidityIndicator
                  valid={
                    cvr === existing?.defaultBillingAddress?.vat &&
                    existing?.defaultBillingAddress?.isCvrValidExternal === false
                      ? false
                      : null
                  }
                  warningTooltip="CVR could not be verified externally"
                />
              </Box>
            </div>
          </div>

          {/* Section 3: Billing | Shipment */}
          {/* Header row — keeps both labels on the same baseline */}
          <div className={styles.billingShippingContainer} style={{ marginBottom: "0.25rem" }}>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 0 }}>
              <h4 className={styles.addressTitle} style={{ margin: 0 }}>Billing:</h4>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={useSameForShipping}
                    onChange={() => setUseSameForShipping(!useSameForShipping)}
                    size="small"
                  />
                }
                label={<Typography variant="body2">Same for shipping</Typography>}
                sx={{ ml: 0, my: 0 }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <h4 className={styles.addressTitle} style={{ margin: 0 }}>Shipment:</h4>
            </div>
          </div>
          <div className={styles.billingShippingContainer}>
            {/* Billing column */}
            <div className={styles.addressColumn}>
              <div>
                <TextField
                  label={"Invoicing email:"}
                  id="billingEmail"
                  size="small"
                  fullWidth
                  required
                  type="email"
                  value={billingEmail}
                  onChange={(e) => setBillingEmail(e.target.value)}
                  error={!!fieldErrors.billingEmail}
                  helperText={fieldErrors.billingEmail ?? ""}
                />
              </div>
              <TextField
                label={"Company street:"}
                id="billingStreet"
                size="small"
                fullWidth
                required
                type="text"
                value={addressName}
                error={!!fieldErrors.billingStreet}
                helperText={fieldErrors.billingStreet ?? ""}
                onChange={(e) => setAddressName(e.target.value)}
                inputProps={{ maxLength: 400 }}
              />
              <TextField
                label={"Company city:"}
                id="billingCity"
                size="small"
                fullWidth
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                InputProps={{
                  inputProps: { maxLength: 50 },
                }}
              />
              <TextField
                label={"Postal code:"}
                id="billingZip"
                size="small"
                fullWidth
                type="text"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                error={!!fieldErrors.billingZip}
                helperText={fieldErrors.billingZip ?? ""}
              />
              <CountrySelect
                value={billingCountryId}
                onChange={setBillingCountryId}
                label="Country"
              />
              <TextField
                label={"Phone:"}
                id="billingPhone"
                size="small"
                fullWidth
                type="text"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                error={!!fieldErrors.billingPhone}
                helperText={fieldErrors.billingPhone ?? ""}
              />
            </div>

            {/* Shipment column */}
            <div className={styles.addressColumn}>
              <div>
                <TextField
                  label={"Shipment email:"}
                  id="shippingEmail"
                  size="small"
                  fullWidth
                  type="email"
                  disabled={useSameForShipping}
                  value={useSameForShipping ? billingEmail : shippingEmail}
                  onChange={(e) => setShippingEmail(e.target.value)}
                  error={!!fieldErrors.shippingEmail}
                  helperText={fieldErrors.shippingEmail ?? ""}
                />
              </div>
              <TextField
                label={"Street:"}
                id="shippingStreet"
                size="small"
                fullWidth
                type="text"
                disabled={useSameForShipping}
                value={useSameForShipping ? addressName : shippingAddressName}
                error={!!fieldErrors.shippingStreet}
                helperText={fieldErrors.shippingStreet ?? ""}
                onChange={(e) => setShippingAddressName(e.target.value)}
                inputProps={{ maxLength: 400 }}
              />
              <TextField
                label={"City:"}
                id="shippingCity"
                size="small"
                fullWidth
                type="text"
                disabled={useSameForShipping}
                value={useSameForShipping ? city : shippingCity}
                onChange={(e) => setShippingCity(e.target.value)}
                InputProps={{
                  inputProps: { maxLength: 50 },
                }}
              />
              <TextField
                label={"Postal code:"}
                id="shippingZip"
                size="small"
                fullWidth
                type="text"
                disabled={useSameForShipping}
                value={useSameForShipping ? zipCode : shippingZipCode}
                onChange={(e) => setShippingZipCode(e.target.value)}
                error={!!fieldErrors.shippingZip}
                helperText={fieldErrors.shippingZip ?? ""}
              />
              <CountrySelect
                value={useSameForShipping ? billingCountryId : shippingCountryId}
                onChange={setShippingCountryId}
                label="Country"
                disabled={useSameForShipping}
              />
              <TextField
                label={"Phone:"}
                id="shippingPhone"
                size="small"
                fullWidth
                type="text"
                disabled={useSameForShipping}
                value={useSameForShipping ? phone : shippingPhone}
                onChange={(e) => setShippingPhone(e.target.value)}
                error={!!fieldErrors.shippingPhone}
                helperText={fieldErrors.shippingPhone ?? ""}
              />
            </div>
          </div>

          {/* Section 4: Contact Person */}
          <h4 className={styles.addressTitle}>Contact Info:</h4>
          <div className={styles.billingShippingContainer}>
            <div className={styles.twoColumnField}>
              <TextField
                label={"First name:"}
                size="small"
                id={"firstName"}
                required
                fullWidth
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                error={!!fieldErrors.firstName}
                helperText={fieldErrors.firstName ?? ""}
                inputProps={{ maxLength: 30 }}
              />
            </div>
            <div className={styles.twoColumnField}>
              <TextField
                label={"Last name:"}
                id={"lastName"}
                size="small"
                required
                fullWidth
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                error={!!fieldErrors.lastName}
                helperText={fieldErrors.lastName ?? ""}
                inputProps={{ maxLength: 30 }}
              />
            </div>
          </div>
          <div className={styles.billingShippingContainer} style={{ marginBottom: "1.5rem" }}>
            {/* <div className={styles.twoColumnField}>
              <TextField
                label={"Job title:"}
                id={"jobTitle"}
                size="small"
                fullWidth
                type="text"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
              />
            </div> */}
            <div className={styles.twoColumnField}>
              <TextField
                label={"Email:"}
                id="contactEmail"
                size="small"
                disabled={!!existing}
                fullWidth
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={!!fieldErrors.contactEmail}
                helperText={fieldErrors.contactEmail ?? ""}
              />
            </div>
          </div>

          {/* Section 5: E-invoicing */}
          <h4 className={styles.addressTitle}>E-invoicing:</h4>
          <div className={styles.billingShippingContainer} style={{ marginBottom: "1.5rem" }}>
            <div className={styles.twoColumnField}>
              <TextField
                label={"Initials (yourReference):"}
                id="initials"
                size="small"
                fullWidth
                type="text"
                value={initials}
                onChange={(e) => setInitials(e.target.value)}
                inputProps={{ maxLength: 10 }}
                helperText="Used as yourReference on e-invoices"
              />
            </div>
            <div className={styles.twoColumnField}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <TextField
                  label={"EAN:"}
                  id="ean"
                  size="small"
                  type="text"
                  fullWidth
                  value={ean}
                  onChange={(e) => setEan(e.target.value)}
                  error={!!fieldErrors.ean}
                  helperText={fieldErrors.ean ?? ""}
                  inputProps={{ maxLength: 13 }}
                />
                <ValidityIndicator
                  valid={ean.length > 0 && validateEAN(ean, billingCountryId) !== undefined ? false : null}
                  warningTooltip="EAN checksum is invalid — check the number"
                />
                <ValidityIndicator
                  valid={
                    ean === existing?.eanNumber &&
                    existing?.isEanValidExternal === false
                      ? false
                      : null
                  }
                  warningTooltip="EAN is not registered in Nemhandel"
                />
                <ValidityIndicator
                  valid={
                    ean === existing?.eanNumber &&
                    existing?.isEanInvoiceDiff === true
                      ? false
                      : null
                  }
                  warningTooltip="EAN differs from the one registered in e-conomic"
                />
              </Box>
            </div>
          </div>
        </form>

        <div className={styles.flRg}>
          <Button
            color={"error"}
            variant="contained"
            sx={{ marginRight: "1em" }}
            onClick={() => handleClose()}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={() => handleSubmit()}>
            {!existing ? "Create" : "Update"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default UserModal;
